import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Images, Loader2, ScanSearch } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { zlecSpisObrazkow, type Pokrycie, type PokrycieWpis } from '@/lib/items-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  POKRYCIE KAFELKÓW GRUP NA AUTOMATACH — na ekranie grup artykułów.
 *
 *  ⚠️⚠️ ZAMÓWIONE 29.10.2026: pokrycie zdjęć w „Zarządzaj zdjęciami" liczyło tylko artykuły,
 *  „może niech to będzie w grupach artykułów". Obrazek grupy jedzie na każdy automat osobno, więc
 *  pytanie „czy wszędzie jest" ma tu tę samą wagę co przy zdjęciu artykułu.
 *
 *  ⚠️ STANY I REGUŁA Z SERWERA (`pokrycie-obrazkow.ts`), etykiety z okna zdjęć (`zdj.stan.*`,
 *  `zdj.stanMaszyny.*`) — dwa ekrany o tym samym pliku nie mogą nazywać tego samego stanu inaczej.
 *
 *  ⚠️ NAPRAWA TO PONOWNA WYSYŁKA Z OKNA OBRAZKA GRUPY (klik w znacznik): plik jedzie na wszystkie
 *  automaty, a te, które go mają, nadpiszą go tym samym. Osobnej „dosyłki brakującym" tu nie ma —
 *  tę robi synchronizacja w „Zarządzaj zdjęciami", która od 27.10.2026 bierze też pliki grup.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function PokrycieObrazkowGrup({ groupId, dane }: { groupId: string; dane: Pokrycie }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const spis = useMutation({
    mutationFn: () => zlecSpisObrazkow(groupId),
    onSuccess: (r) => {
      if (r.zadan > 0) {
        toast.success(t('zdj.spisZlecony').replace('{zadan}', String(r.zadan)).replace('{wezlow}', String(r.wezlow)));
      } else if (r.zaStare.length > 0) {
        toast.error(t('zdj.spisZaStareWszystkie').replace('{wezly}', r.zaStare.join(', ')).replace('{wersja}', r.wersjaWymagana));
      } else {
        toast.error(t('zdj.spisNikt'));
      }
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  const ile = (stan: PokrycieWpis['stan']) => dane.grupy.filter((g) => g.stan === stan).length;
  const braki = ile('BRAKI');
  const rozjazd = ile('ROZJAZD');
  const wToku = dane.maszyny.some((m) => m.wToku);
  /**
   * STAN SPRAWDZENIA ZAMIAST ZNACZNIKA „NIE SPRAWDZONO" — przy pierwszym obejrzeniu (29.10.2026)
   * wiersz mówił to samo dwa razy („nie sprawdzono" na znaczniku, „nie sprawdzano" obok). Data
   * to NAJSTARSZY spis: „sprawdzono" mówi o najsłabszym ogniwie, nie o najświeższym.
   */
  const spisy = dane.maszyny.map((m) => m.spisAt);
  const zmierzone = spisy.filter((s): s is string => s !== null);
  const sprawdzenie =
    zmierzone.length === 0
      ? t('igrp.pokr.nieSprawdzano')
      : zmierzone.length < spisy.length
        ? t('igrp.pokr.czesciowo')
        : t('igrp.pokr.sprawdzono').replace('{kiedy}', new Date(zmierzone.reduce((a, b) => (b < a ? b : a))).toLocaleString('pl-PL'));
  /** „Na wszystkich" tylko wtedy, gdy KAŻDA maszyna jest zmierzona — inaczej zielone kłamałoby o niewiadomej. */
  const wszedzie = braki === 0 && rozjazd === 0 && zmierzone.length > 0 && zmierzone.length === spisy.length;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2 text-sm">
      <Images className="size-4 shrink-0 text-muted-foreground" />
      <span className="font-medium">{t('igrp.pokr.tytul')}</span>
      {braki > 0 && <Badge variant="destructive">{t('igrp.pokr.braki').replace('{n}', String(braki))}</Badge>}
      {rozjazd > 0 && <Badge variant="warning">{t('igrp.pokr.rozjazd').replace('{n}', String(rozjazd))}</Badge>}
      {wszedzie && (
        <span className="flex items-center gap-1 text-[var(--success)]">
          <Check className="size-3.5" />
          {t('zdj.stan.WSZEDZIE')}
        </span>
      )}
      <span className="text-xs text-muted-foreground">{sprawdzenie}</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="ml-auto"
        disabled={spis.isPending || wToku}
        title={t('zdj.spisOpis')}
        onClick={() => spis.mutate()}
      >
        {spis.isPending || wToku ? <Loader2 className="animate-spin" /> : <ScanSearch />}
        {t('zdj.spis')}
      </Button>
    </div>
  );
}

/**
 * Znacznik przy grupie: czerwony, gdy któremuś automatowi brakuje pliku; żółty, gdy któryś ma inny.
 * Przy „wszędzie" i „nie sprawdzono" milczy — to mówi wiersz nad drzewem, a nie każda grupa z osobna.
 */
export function ZnacznikPokryciaGrupy({ wpis, onClick }: { wpis: PokrycieWpis | undefined; onClick: () => void }) {
  const { t } = useI18n();
  if (wpis === undefined || (wpis.stan !== 'BRAKI' && wpis.stan !== 'ROZJAZD')) return null;
  const braki = wpis.stan === 'BRAKI';
  /** Dymek wymienia każdy automat z kłopotem; liczba na znaczniku liczy tylko ten rodzaj, który daje kolor. */
  const opis = wpis.maszyny
    .filter((m) => m.stan !== 'MA' && m.stan !== 'NIEZMIERZONE')
    .map((m) => `${m.automat?.nazwa ?? m.nodeId}: ${t(`zdj.stanMaszyny.${m.stan}`)}`)
    .join('\n');
  const ile = wpis.maszyny.filter((m) =>
    braki ? m.stan === 'BRAK' || m.stan === 'PUSTY_PLIK' : m.stan === 'INNY_PLIK' || m.stan === 'INNY_ROZMIAR',
  ).length;
  return (
    <button type="button" className="self-center" title={opis} onClick={onClick}>
      <Badge variant={braki ? 'destructive' : 'warning'}>
        {(braki ? t('igrp.pokr.brakNa') : t('igrp.pokr.innyNa')).replace('{n}', String(ile))}
      </Badge>
    </button>
  );
}
